import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2, Database, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";

/**
 * RecruiterCardPage — public, tokenized read of a single pitch-mechanics
 * capture. NO AUTH required to view. Designed to be the artifact athletes
 * voluntarily share with recruiters: the kinematic story + linked ball-flight
 * rollup at one URL.
 *
 *   /share/capture/{token}
 *
 * Backend: GET /api/share/capture/{token} (sanitized payload only — no raw
 * landmarks, no internal IDs).
 */
const METRIC_DISPLAY = [
  { k: "peak_ext_rotation_deg", label: "Peak ER", unit: "°" },
  { k: "stride_length_pct", label: "Stride", unit: "%" },
  { k: "trunk_tilt_release_deg", label: "Trunk lean", unit: "°" },
  { k: "hip_shoulder_separation_deg", label: "Hip-shoulder sep", unit: "°" },
  { k: "front_knee_flex_br_deg", label: "Knee @ BR", unit: "°" },
  { k: "shoulder_abduction_deg", label: "Shoulder abd", unit: "°" },
  { k: "head_tilt_max_deg", label: "Head tilt (15°)", unit: "°" },
  { k: "head_drift_in", label: "Head drift", unit: "″" },
];

export default function RecruiterCardPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/share/capture/${token}`);
        setData(d);
      } catch (e) {
        setErr(e?.response?.status === 404 ? "This share link has expired or been revoked." : "Could not load this capture.");
      }
    })();
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--ink-1)" }}>
        <div className="text-center max-w-md">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-400" />
          <h1 className="text-2xl font-display font-black text-white mt-4">{err}</h1>
          <Link to="/" className="text-cyan-300 underline text-sm mt-4 inline-block">Visit ArmIQ →</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--ink-1)" }}>
        <Loader2 className="h-6 w-6 animate-spin text-white/50" />
      </div>
    );
  }

  const { athlete, team, capture, norms, telemetry_rollups, captured_at } = data;
  const totalPitches = (telemetry_rollups || []).reduce((a, t) => a + (t.summary?.pitches || 0), 0);
  const peakVelo = Math.max(...(telemetry_rollups || []).map((t) => t.summary?.max_velocity_mph || 0), 0) || null;
  const avgVelo = (() => {
    const vals = (telemetry_rollups || []).map((t) => t.summary?.avg_velocity_mph).filter((x) => x != null);
    if (!vals.length) return null;
    return +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  })();
  const date = captured_at ? new Date(captured_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
  const flagCount = (capture?.flags || []).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="recruiter-card-page">
      {/* Header strip */}
      <div className="border-b border-[var(--ink-border)] bg-[var(--ink-0)]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <Link to="/" className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/55 hover:text-white inline-flex items-center gap-1">
            Try ArmIQ <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        {/* HERO */}
        <div data-testid="recruiter-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.25em] uppercase border"
               style={{ backgroundColor: "rgba(217,4,41,0.12)", borderColor: "rgba(217,4,41,0.35)", color: "var(--brand-secondary)" }}>
            Pitch Mechanics · 2D + 3D Capture
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white mt-3">
            {athlete?.name || "Athlete"}
          </h1>
          <div className="mt-2 text-sm text-white/55 flex flex-wrap items-center gap-x-4 gap-y-1">
            {athlete?.position && <span>{athlete.position}</span>}
            {athlete?.year && <span>{athlete.year}</span>}
            {capture?.handedness && <span className="capitalize">{capture.handedness}-handed</span>}
            {team?.name && <span>· {team.name}</span>}
            {date && <span>· captured {date}</span>}
          </div>
        </div>

        {/* TOP METRICS */}
        {(peakVelo || avgVelo || totalPitches) > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recruiter-top-metrics">
            {peakVelo && <BigStat label="Peak velocity" value={peakVelo} unit="mph" />}
            {avgVelo && <BigStat label="Avg velocity" value={avgVelo} unit="mph" />}
            {capture?.peak_ext_rotation_deg && <BigStat label="Peak ER" value={capture.peak_ext_rotation_deg} unit="°" />}
            {capture?.stride_length_pct && <BigStat label="Stride length" value={capture.stride_length_pct} unit="%" />}
          </div>
        )}

        {/* KINEMATIC CARDS */}
        <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5" data-testid="recruiter-kinematics">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Kinematic capture · elite-window bands</div>
            <div className={`text-[10px] uppercase tracking-wider font-bold ${flagCount === 0 ? "text-emerald-300" : "text-amber-300"}`}>
              {flagCount === 0 ? "All in band" : `${flagCount} flag${flagCount === 1 ? "" : "s"}`}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {METRIC_DISPLAY.map(({ k, label, unit }) => {
              const v = capture?.[k];
              if (v == null) return null;
              const norm = norms?.[k];
              const out = norm && (v < norm.low || v > norm.high);
              return (
                <div key={k} className="rounded p-3 bg-black/30 border border-white/5" data-testid={`recruiter-metric-${k}`}>
                  <div className="text-[10px] text-white/55 uppercase tracking-wider">{label}</div>
                  <div className="flex items-baseline gap-1.5 mt-0.5">
                    <span className={`font-display font-bold text-base ${out ? "text-amber-300" : "text-white"}`}>{v}{unit}</span>
                    {!out && norm && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
                  </div>
                  {norm && <div className="text-[10px] text-white/35">norm {norm.low}–{norm.high}</div>}
                </div>
              );
            })}
          </div>
        </Card>

        {/* LINKED VENDOR TELEMETRY ROLLUPS */}
        {Array.isArray(telemetry_rollups) && telemetry_rollups.length > 0 && (
          <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5" data-testid="recruiter-telemetry">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] flex items-center gap-1.5">
                <Database className="h-3 w-3" /> Vendor Telemetry · {totalPitches} pitches across {telemetry_rollups.length} session{telemetry_rollups.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="space-y-2">
              {telemetry_rollups.map((t, i) => {
                const s = t.summary || {};
                return (
                  <div key={i} className="rounded p-3 bg-black/30 border border-white/10 flex flex-wrap items-center justify-between gap-3" data-testid={`recruiter-rollup-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded bg-[var(--brand-secondary)]/15 text-white">
                        {(s.source || t.source || "?").toUpperCase()}
                      </span>
                      <span className="text-sm text-white font-semibold">{s.pitches || 0} pitches</span>
                    </div>
                    <div className="flex items-baseline gap-3 text-[11px] font-mono-num text-white/75">
                      {s.max_velocity_mph != null && <span><span className="text-white/40">peak</span> {s.max_velocity_mph} mph</span>}
                      {s.avg_velocity_mph != null && <span><span className="text-white/40">avg</span> {s.avg_velocity_mph}</span>}
                      {s.avg_spin_rpm != null && <span><span className="text-white/40">spin</span> {Math.round(s.avg_spin_rpm)}</span>}
                      {s.avg_extension_ft != null && <span><span className="text-white/40">ext</span> {s.avg_extension_ft}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* FOOTER CTA */}
        <div className="text-center pt-2">
          <Link to="/" className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/45 hover:text-white" data-testid="recruiter-footer-cta">
            Powered by ArmIQ · the complete arm-health operating system →
          </Link>
        </div>
      </div>
    </div>
  );
}

function BigStat({ label, value, unit }) {
  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-4">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/55">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span className="font-display font-black text-4xl text-white font-mono-num">{value}</span>
        <span className="text-[10px] uppercase tracking-wider text-white/45">{unit}</span>
      </div>
    </Card>
  );
}
